<script lang="ts">
  import {Button} from 'flowbite-svelte';
  import {cn} from 'Catalyst/cn';
  import {Icon, type IconTVProps} from 'Catalyst/components/Icon';
  import {
    buttonVariantsSize24dpIconDensity,
    buttonVariantsSize24dpIconSize,
    buttonVariantsSize32dpIconDensity,
    buttonVariantsSize32dpIconSize,
    buttonVariantsSize36dpIconDensity,
    buttonVariantsSize36dpIconSize,
    buttonVariantsSize40dpIconDensity,
    buttonVariantsSize40dpIconSize,
    buttonVariantsSize48dpIconDensity,
    buttonVariantsSize48dpIconSize,
    buttonVariantsSize56dpIconDensity,
    buttonVariantsSize56dpIconSize,
  } from 'Catalyst/tokens/output/button';
  import {type ButtonProps, buttonTvProps} from '.';

  import {getContext} from 'svelte';
  import type {CatalystConfig} from 'Catalyst/components/ThemeContext';
  import type {Writable} from 'svelte/store';

  // eslint-disable-next-line no-unused-vars
  type $$Props = ButtonProps;

  const iconPropsMap = {
    '24dp': {
      density: buttonVariantsSize24dpIconDensity,
      size: buttonVariantsSize24dpIconSize,
    },
    '32dp': {
      density: buttonVariantsSize32dpIconDensity,
      size: buttonVariantsSize32dpIconSize,
    },
    '36dp': {
      density: buttonVariantsSize36dpIconDensity,
      size: buttonVariantsSize36dpIconSize,
    },
    '40dp': {
      density: buttonVariantsSize40dpIconDensity,
      size: buttonVariantsSize40dpIconSize,
    },
    '48dp': {
      density: buttonVariantsSize48dpIconDensity,
      size: buttonVariantsSize48dpIconSize,
    },
    '56dp': {
      density: buttonVariantsSize56dpIconDensity,
      size: buttonVariantsSize56dpIconSize,
    },
  };

  const catalystConfig = getContext<Writable<CatalystConfig>>('catalystConfig');

  export let label: ButtonProps['label'] = 'Label';
  export let leadingIcon: ButtonProps['leadingIcon'] = '';
  export let size: ButtonProps['size'] =
    $catalystConfig?.defaultVariants?.size ||
    buttonTvProps.defaultVariants.size;
  export let style: ButtonProps['style'] =
    ($catalystConfig?.defaultVariants?.style as ButtonProps['style']) ||
    buttonTvProps.defaultVariants.style;
  export let semantic: ButtonProps['semantic'] =
    buttonTvProps.defaultVariants.semantic;
  export let trailingIcon: ButtonProps['trailingIcon'] = '';
  export let disabled: boolean = false;
  export let href: string = null;
  export let target: string = null;

  export let labelClass: ButtonProps['labelClass'] = {class: ''};

  let actualSize: any;

  $: switch (size) {
    case 'xs':
      actualSize = '32dp';
      break;
    case 'sm':
      actualSize = '36dp';
      break;
    case 'md':
      actualSize = '40dp';
      break;
    case 'lg':
      actualSize = '56dp';
      break;
    default:
      actualSize = size;
      break;
  }

  $: buttonTvSlots = buttonTvProps({
    size: actualSize,
    style,
    semantic,
    disabled,
  });

  $: iconProps = iconPropsMap[actualSize] as IconTVProps;

  $: klass = $$restProps.class;
</script>

<Button
  {...$$restProps}
  class={cn(buttonTvSlots.base(), klass)}
  {href}
  {target}
  on:change
  on:click
  on:keydown
  on:keyup
  on:mouseenter
  on:mouseleave
  on:touchcancel
  on:touchend
  on:touchstart
  {disabled}
>
  {#if leadingIcon}
    <Icon
      name={leadingIcon}
      size={iconProps.size}
      density={iconProps.density}
      class={buttonTvSlots.icon()}
    />
  {/if}
  <slot>
    {#if label}
      <span class={labelClass.class}>{label}</span>
    {/if}
  </slot>
  {#if trailingIcon}
    <Icon
      name={trailingIcon}
      size={iconProps.size}
      density={iconProps.density}
      class={buttonTvSlots.icon()}
    />
  {/if}
</Button>
